import { useState } from "react";
import { Link, router } from "@inertiajs/react";
import { ArrowLeft, CalendarPlus, Package as PackageIcon, Phone, Mail, MessageCircle } from "lucide-react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FichaBody } from "@/components/ficha/FichaBody";
import { clientMetrics } from "@/components/ficha/metrics";
import type {
  BookingSummary,
  Client,
  ClientPackage,
  PageProps,
} from "@/types";

interface PackageOfferOption {
  id: number;
  service_id: number;
  service_name: string;
  name: string;
  sessions_included: number;
  total_price_cents: number;
  currency: string;
}

interface ClientShowProps extends PageProps {
  client: Client;
  client_packages: ClientPackage[];
  package_offers: PackageOfferOption[];
  can_activate_packages: boolean;
  can_cancel_packages: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  analytics: BookingSummary;
}

function money(cents: number, currency: string) {
  return `$${(cents / 100).toLocaleString("es-MX")} ${currency}`;
}

const redemptionLabels = {
  reserved: "Agendada",
  consumed: "Completada",
  released: "Cancelada · sesión devuelta",
  forfeited: "No show · sesión utilizada",
} as const;

export default function ClientShow({
  client,
  client_packages: initialPackages,
  package_offers,
  can_activate_packages,
  can_cancel_packages,
  can_edit = false,
  can_delete = false,
  analytics,
  current_user,
  current_organization,
  current_business,
}: ClientShowProps) {
  const orgSlug = current_organization?.slug || "";
  const [packages, setPackages] = useState(initialPackages);
  const [offerId, setOfferId] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");

  const csrfToken =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || "";

  const activatePackage = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!offerId) return;
    setSaving(true);
    setActionError("");
    try {
      const response = await fetch(
        `/${orgSlug}/clients/${client.id}/packages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            client_package: {
              service_package_offer_id: Number(offerId),
              payment_reference: paymentReference || null,
              purchase_source: "dashboard",
            },
          }),
        },
      );
      const payload = await response.json();
      if (!response.ok) {
        setActionError(
          payload.error || "No se pudo activar el paquete. Revisa los datos.",
        );
        return;
      }
      setPackages((current) => [payload.client_package, ...current]);
      setOfferId("");
      setPaymentReference("");
    } finally {
      setSaving(false);
    }
  };

  const cancelPackage = async (clientPackage: ClientPackage) => {
    if (
      !confirm(
        `¿Cancelar "${clientPackage.name}"? No se borrará su historial.`,
      )
    )
      return;
    setActionError("");
    const response = await fetch(
      `/${orgSlug}/clients/${client.id}/packages/${clientPackage.id}/cancel`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setActionError(
        payload.error ||
          "No se pudo cancelar el paquete. Revisa sus sesiones agendadas.",
      );
      return;
    }
    setPackages((current) =>
      current.map((item) =>
        item.id === clientPackage.id ? payload.client_package : item,
      ),
    );
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/clients`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a clientes
          </Link>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">{client.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {client.phone}
                </span>
                {client.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    {client.email}
                  </span>
                )}
                {client.whatsapp_opted_in && (
                  <span className="flex items-center gap-1 text-green-500">
                    <MessageCircle className="h-3.5 w-3.5" />
                    WhatsApp
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {can_edit && (
                <Link href={`/${orgSlug}/clients/${client.id}/edit`}>
                  <Button variant="outline">Editar</Button>
                </Link>
              )}
              {can_delete && (
                <Button
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => {
                    if (confirm(`¿Estás seguro de eliminar a "${client.name}"?`)) {
                      router.delete(`/${orgSlug}/clients/${client.id}`);
                    }
                  }}
                >
                  Eliminar
                </Button>
              )}
              <Link href={`/${orgSlug}/calendar?client_id=${client.id}`}>
                <Button>
                  <CalendarPlus className="mr-2 h-4 w-4" />
                  Agendar
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <FichaBody
          analytics={analytics}
          timezone={current_business?.timezone || "America/Mexico_City"}
          metrics={clientMetrics(
            analytics,
            current_business?.timezone || "America/Mexico_City",
          )}
          atRisk={analytics.at_risk}
          hrefFor={({ period, pastPage }) => {
            const params = new URLSearchParams();
            params.set("period", period || analytics.period);
            if (pastPage && pastPage > 1) params.set("past_page", String(pastPage));
            return `/${orgSlug}/clients/${client.id}?${params.toString()}`;
          }}
        />

        <h2 className="text-lg font-semibold">Paquetes</h2>

        {actionError && (
          <div
            role="alert"
            className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            {actionError}
          </div>
        )}

        {can_activate_packages && (
          <form
            onSubmit={activatePackage}
            className="rounded-xl border border-border bg-card p-4 sm:p-6"
          >
            <h2 className="mb-1 font-semibold">Activar paquete pagado</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Esto crea un saldo de sesiones; no crea citas automáticamente.
            </p>
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
              <div className="space-y-2">
                <Label htmlFor="package_offer">Oferta exacta</Label>
                <select
                  id="package_offer"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={offerId}
                  onChange={(event) => setOfferId(event.target.value)}
                  required
                >
                  <option value="">Seleccionar paquete</option>
                  {package_offers.map((offer) => (
                    <option key={offer.id} value={offer.id}>
                      {offer.service_name} · {offer.name} ·{" "}
                      {money(offer.total_price_cents, offer.currency)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_reference">
                  Referencia de pago (opcional)
                </Label>
                <Input
                  id="payment_reference"
                  value={paymentReference}
                  onChange={(event) => setPaymentReference(event.target.value)}
                  placeholder="Efectivo, transferencia, folio..."
                />
              </div>
              <Button
                type="submit"
                className="self-end"
                disabled={saving || !offerId}
              >
                {saving ? "Activando..." : "Activar"}
              </Button>
            </div>
          </form>
        )}

        <section className="space-y-3">
          {packages.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground">
              Este cliente todavía no tiene paquetes.
            </div>
          ) : (
            packages.map((clientPackage) => (
              <article
                key={clientPackage.id}
                className="rounded-xl border border-border bg-card p-4 sm:p-6"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <PackageIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-semibold">{clientPackage.name}</h2>
                        <span
                          className={
                            packageCanBeScheduled(clientPackage)
                              ? "rounded-full bg-green-500/10 px-2 py-0.5 text-xs text-green-600"
                              : "rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                          }
                        >
                          {packageDisplayStatus(clientPackage)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {clientPackage.service_name} ·{" "}
                        {clientPackage.business_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {packageCanBeScheduled(clientPackage) &&
                      clientPackage.business_id === current_business?.id && (
                        <Link
                          href={`/${orgSlug}/calendar?client_id=${client.id}&service_id=${clientPackage.service_id}&client_package_id=${clientPackage.id}`}
                        >
                          <Button size="sm">
                            <CalendarPlus className="mr-2 h-4 w-4" />
                            Agendar sesión
                          </Button>
                        </Link>
                      )}
                    {packageCanBeScheduled(clientPackage) &&
                      clientPackage.business_id !== current_business?.id && (
                        <span className="max-w-52 text-right text-xs text-muted-foreground">
                          Cambia a {clientPackage.business_name} para agendar.
                        </span>
                      )}
                    {can_cancel_packages &&
                      clientPackage.status === "active" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => cancelPackage(clientPackage)}
                        disabled={clientPackage.sessions_scheduled > 0}
                        title={
                          clientPackage.sessions_scheduled > 0
                            ? "Cancela primero las sesiones agendadas"
                            : undefined
                        }
                      >
                        Cancelar paquete
                      </Button>
                    )}
                  </div>
                </div>

                {clientPackage.sessions_scheduled > 0 &&
                  can_cancel_packages &&
                  clientPackage.status === "active" && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Para cancelar el paquete, cancela primero sus sesiones
                      agendadas. Cada cancelación devuelve la sesión.
                    </p>
                  )}

                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Balance label="Disponibles" value={clientPackage.sessions_available} />
                  <Balance label="Agendadas" value={clientPackage.sessions_scheduled} />
                  <Balance label="Completadas" value={clientPackage.sessions_completed} />
                  <Balance label="No show" value={clientPackage.sessions_forfeited} />
                </div>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    Total:{" "}
                    {money(
                      clientPackage.total_price_cents,
                      clientPackage.currency,
                    )}
                  </span>
                  <span>
                    Activado:{" "}
                    {new Date(clientPackage.activated_at).toLocaleDateString(
                      "es-MX",
                    )}
                  </span>
                  <span>
                    Vigencia:{" "}
                    {clientPackage.expires_at
                      ? new Date(clientPackage.expires_at).toLocaleDateString(
                          "es-MX",
                        )
                      : "Sin vencimiento"}
                  </span>
                </div>

                {clientPackage.next_linked_appointment && (
                  <div className="mt-4 rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
                    <span className="font-medium">Próxima sesión:</span>{" "}
                    {new Date(
                      clientPackage.next_linked_appointment.starts_at,
                    ).toLocaleString("es-MX", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </div>
                )}

                {clientPackage.redemptions.length > 0 && (
                  <div className="mt-5 border-t border-border pt-4">
                    <h3 className="mb-2 text-sm font-medium">
                      Historial de sesiones
                    </h3>
                    <div className="space-y-2">
                      {clientPackage.redemptions.map((redemption) => (
                        <div
                          key={redemption.id}
                          className="flex flex-col gap-1 rounded-lg bg-muted/40 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
                        >
                          <span>
                            {redemptionLabels[redemption.status]}
                            {redemption.reason
                              ? ` · ${redemption.reason}`
                              : ""}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(
                              redemption.booking_starts_at,
                            ).toLocaleString("es-MX", {
                              dateStyle: "medium",
                              timeStyle: "short",
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

function Balance({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-muted/50 p-3">
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function packageCanBeScheduled(clientPackage: ClientPackage) {
  const expired =
    clientPackage.expires_at &&
    new Date(clientPackage.expires_at).getTime() <= Date.now();
  return (
    clientPackage.status === "active" &&
    !expired &&
    clientPackage.sessions_available > 0
  );
}

function packageDisplayStatus(clientPackage: ClientPackage) {
  if (clientPackage.status === "cancelled") return "Cancelado";
  if (
    clientPackage.expires_at &&
    new Date(clientPackage.expires_at).getTime() <= Date.now()
  )
    return "Vencido";
  if (clientPackage.sessions_available <= 0) return "Agotado";
  return "Activo";
}
